import { gql } from 'graphql-request';

export const TASK_FIELDS = gql`
  fragment TaskFields on TaskDto {
    taskId
    name
    description
    taskType
    otherType
    manHours
    startTime
    endTime
    timeConstraint
    typeOfTimeConstraint
    presedentTaskId
    presedentEventId
    projectId
    status
    parentTaskId
    superintendentId
    note
    budget
    project {
      projectId
      name
      address
    }
    documents {
      documentId
      name
      url
      delegateOrganizationId
    }
    crews {
      crewId
      name
      employees {
        employeeId
        user {
          userId
          firstName
          lastName
          avatar
          role
        }
      }
    }
    materials {
      materialId
      name
      supplier
      amount
      amountType
      amountSystem
      costPerUnit
      pricePerUnit
      chargeToClient
      time
    }
    equipments {
      equipmentId
      name
      supplierOwner
      hoursUsed
      amount
      costPerHour
      pricePerHour
      chargeToClient
      time
    }
    delegateTaskId
    delegateOrganizationId
    delegateOrganization {
      organizationId
      name
    }
    originalTaskId
    originalProjectId
    originalOrganizationId
    originalOrganization {
      organizationId
      name
    }
    unassigned
  }
`;

export const TASK_STATS_FIELDS = gql`
  fragment TaskStatusFields on StatsDto {
    totalTasks
    completedTasks
  }
`;

export const getTaskStats = gql`
  query getTaskStats($projectId: String!) {
    getTaskStats(projectId: $projectId) {
      ...TaskStatusFields
    }
  }
  ${TASK_STATS_FIELDS}
`;

export const getAllMaterialsAndEquipments = gql`
  query getAllMaterialsAndEquipments($projectId: String!) {
    getAllMaterialsAndEquipments(projectId: $projectId) {
      equipments {
        equipmentId
        name
        supplierOwner
        time
        hoursUsed
        amount
        costPerHour
        pricePerHour
        taskId
        unitCost
        unitPrice
        totalCost
        totalPrice
        isOwnItem
        taskName
        originalProjectId
        originalOrganizationName
        unassigned
        chargeToClient
        displayOrganizationName
        source
      }
      materials {
        materialId
        name
        supplier
        time
        amount
        amountType
        costPerUnit
        pricePerUnit
        taskId
        unitCost
        unitPrice
        totalCost
        totalPrice
        isOwnItem
        taskName
        originalProjectId
        originalOrganizationName
        unassigned
        chargeToClient
        displayOrganizationName
        source
      }
    }
  }
`;

export const getTasks = gql`
  query getTasks($projectId: String!) {
    getTasks(projectId: $projectId) {
      ...TaskFields
    }
  }
  ${TASK_FIELDS}
`;

// Sprint 12, story-054/055 (U11/N2). The viewer-scoped approvals inbox.
export const pendingApprovals = gql`
  query pendingApprovals($organizationId: String!) {
    pendingApprovals(organizationId: $organizationId) {
      taskId
      taskName
      projectId
      projectName
      kind
      approveOutcome
      requestedByName
      enteredApprovalAt
    }
  }
`;

export const getTasksByOriginalOrganizationId = gql`
  query getTasksByOriginalOrganizationId($originalOrganizationId: String!) {
    getTasksByOriginalOrganizationId(
      originalOrganizationId: $originalOrganizationId
    ) {
      ...TaskFields
    }
  }
  ${TASK_FIELDS}
`;

export const getTasksByOrganizationId = gql`
  query getTasksByOrganizationId($organizationId: String!) {
    getTasksByOrganizationId(organizationId: $organizationId) {
      ...TaskFields
    }
  }
  ${TASK_FIELDS}
`;

export const getTaskById = gql`
  query getTaskById($taskId: String!) {
    getTaskById(taskId: $taskId) {
      ...TaskFields
    }
  }
  ${TASK_FIELDS}
`;

export const createTask = gql`
  mutation createTask($task: TaskCreateInput!) {
    createTask(task: $task) {
      ...TaskFields
    }
  }
  ${TASK_FIELDS}
`;

export const createSubtask = gql`
  mutation createSubtask($task: TaskCreateInput!, $parentTaskId: String!) {
    createSubtask(task: $task, parentTaskId: $parentTaskId) {
      ...TaskFields
    }
  }
  ${TASK_FIELDS}
`;

export const updateTask = gql`
  mutation updateTask($task: TaskUpdateInput!) {
    updateTask(task: $task) {
      ...TaskFields
    }
  }
  ${TASK_FIELDS}
`;

export const updateTaskStatus = gql`
  mutation updateTaskStatus($input: StatusHistoryCreateInput!) {
    updateTaskStatus(input: $input) {
      taskId
      name
      status
      createdAt
    }
  }
`;

/**
 * Approve a task waiting on this organization. The server decides what approving means from the
 * task's own status and delegation — complete it, send it to the client, or forward it up a
 * delegation chain — so the client never picks a target status. Replaces `completeTask` /
 * `forApprovalTask`, which chose from the approval kind alone and approved an in-house task into
 * CLIENT_APPROVAL.
 */
export const approveTask = gql`
  mutation approveTask($id: String!) {
    approveTask(id: $id) {
      ...TaskFields
    }
  }
  ${TASK_FIELDS}
`;

export const updatePrecedingTasks = gql`
  mutation updatePrecedingTasks(
    $taskId: String!
    $precedingTaskId: [String!]!
  ) {
    updatePrecedingTasks(taskId: $taskId, precedingTaskId: $precedingTaskId) {
      ...TaskFields
    }
  }
  ${TASK_FIELDS}
`;

export const rejectTask = gql`
  mutation rejectTask($id: String!, $note: String!) {
    rejectTask(id: $id, note: $note) {
      ...TaskFields
    }
  }
  ${TASK_FIELDS}
`;

export const deleteTask = gql`
  mutation deleteTask($id: String!) {
    deleteTask(id: $id) {
      affected
      messages
    }
  }
`;

export const cancelDelegation = gql`
  mutation cancelDelegation($taskId: String!) {
    cancelDelegation(taskId: $taskId) {
      ...TaskFields
    }
  }
  ${TASK_FIELDS}
`;

const TasksGraphQL = {
  queries: {
    getTasks,
    getAllMaterialsAndEquipments,
    getTaskStats,
    getTasksByOriginalOrganizationId,
    getTasksByOrganizationId,
    getTaskById,
    pendingApprovals,
  },
  mutations: {
    createTask,
    createSubtask,
    updateTask,
    updateTaskStatus,
    deleteTask,
    approveTask,
    rejectTask,
    updatePrecedingTasks,
    cancelDelegation,
  },
};

export default TasksGraphQL;
